import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { outreachApi } from '../api/outreachApi';
import { contactValues, hasSpintax, renderPreview } from '../campaignPreview';
import InfoHint from './InfoHint';
import MailboxPicker from './MailboxPicker';

// Must match the exact set the dispatcher fills in (outreach/app/campaigns/dispatcher.py
// _variable_values) - custom_fields from a contact import work too, just aren't listed here.
const VARIABLE_FIELDS = [
  { key: 'first_name', labelKey: 'outreach.campaign.variables.firstName' },
  { key: 'last_name', labelKey: 'outreach.campaign.variables.lastName' },
  { key: 'company', labelKey: 'outreach.campaign.variables.company' },
  { key: 'title', labelKey: 'outreach.campaign.variables.jobTitle' },
  { key: 'country', labelKey: 'outreach.campaign.variables.country' },
  { key: 'website', labelKey: 'outreach.campaign.variables.website' },
  { key: 'email', labelKey: 'outreach.campaign.variables.email' },
];

const DEFAULT_SCHEDULE = {
  timezone: 'Europe/Kyiv',
  days: [0, 1, 2, 3, 4],
  window_start: '09:00:00',
  window_end: '18:00:00',
  min_gap_seconds: 540,
  jitter_seconds: 300,
};

// Opens are ~half Apple MPP noise and the pixel itself costs
// deliverability, so tracking starts off.
const DEFAULT_TRACKING = { open: false, click: false, unsub_link: false };

const emptyStep = (index) => ({
  index,
  delay_days: index === 0 ? 0 : 3,
  same_thread: true,
  variants: [{ subject: '', body: '', weight: 1, enabled: true }],
});

export default function CampaignWizard({ accounts, lists, onDone, onCancel }) {
  const { t } = useTranslation('common');

  const [name, setName] = useState('');
  // One mailbox, not all of them: sending a first campaign from every
  // connected address at once is never what someone meant to tick.
  const [accountIds, setAccountIds] = useState(accounts.length ? [accounts[0].id] : []);
  const [steps, setSteps] = useState([emptyStep(0)]);
  const [listId, setListId] = useState(lists[0]?.id || '');
  const [firstNameFallback, setFirstNameFallback] = useState('there');
  const [optOutLine, setOptOutLine] = useState(t('outreach.campaign.defaultOptOut'));
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  // Setter comes back with the tracking toggles below.
  const [tracking] = useState(DEFAULT_TRACKING);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Which step/field the {{ autocomplete popup is open for, and the token
  // span (start/end) in that field's text it will replace on selection.
  const [suggest, setSuggest] = useState(null);
  // Per step, whichever of subject/body was focused last - a chip click
  // inserts there, since the button click itself blurs the real textarea.
  const [activeFieldByStep, setActiveFieldByStep] = useState({});
  const [customFieldKeys, setCustomFieldKeys] = useState([]);
  const [sampleContact, setSampleContact] = useState(null);
  const [previewStep, setPreviewStep] = useState(0);
  const subjectRefs = useRef([]);
  const bodyRefs = useRef([]);

  useEffect(() => {
    if (!listId) {
      setCustomFieldKeys([]);
      setSampleContact(null);
      return;
    }
    outreachApi.listCustomFieldKeys(listId).then(setCustomFieldKeys);
    outreachApi
      .listContacts({ listId, limit: 1 })
      .then((data) => setSampleContact(data.contacts[0] || null));
  }, [listId]);

  const availableVariables = [
    ...VARIABLE_FIELDS,
    ...customFieldKeys.map((key) => ({ key, label: key })),
  ];
  const variableLabel = (field) => (field.labelKey ? t(field.labelKey) : field.label);

  const updateStep = (index, patch) =>
    setSteps(steps.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  const updateVariant = (stepIndex, patch) =>
    setSteps(
      steps.map((s, i) =>
        i === stepIndex ? { ...s, variants: [{ ...s.variants[0], ...patch }] } : s
      )
    );

  const fieldRefs = (field) => (field === 'subject' ? subjectRefs : bodyRefs);
  const fieldValue = (stepIndex, field) => steps[stepIndex].variants[0][field];

  const insertToken = (stepIndex, field, token) => {
    const el = fieldRefs(field).current[stepIndex];
    const value = fieldValue(stepIndex, field);
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    updateVariant(stepIndex, { [field]: value.slice(0, start) + token + value.slice(end) });
    const caret = start + token.length;
    requestAnimationFrame(() => {
      const node = fieldRefs(field).current[stepIndex];
      if (node) node.setSelectionRange(caret, caret);
      if (node) node.focus();
    });
  };

  // Live-filters as the user types after "{{", e.g. "{{comp" -> matches company.
  const handleFieldInput = (stepIndex, field, e) => {
    const value = e.target.value;
    updateVariant(stepIndex, { [field]: value });
    const uptoCaret = value.slice(0, e.target.selectionStart);
    const match = uptoCaret.match(/\{\{(\w*)$/);
    if (!match) {
      setSuggest(null);
      return;
    }
    const query = match[1].toLowerCase();
    const matches = availableVariables.filter((f) => f.key.startsWith(query));
    setSuggest(matches.length ? { stepIndex, field, start: match.index, end: e.target.selectionStart, matches } : null);
  };

  const applySuggestion = (item) => {
    const { stepIndex, field, start, end } = suggest;
    const value = fieldValue(stepIndex, field);
    const token = `{{${item.key}}}`;
    updateVariant(stepIndex, { [field]: value.slice(0, start) + token + value.slice(end) });
    setSuggest(null);
    const caret = start + token.length;
    requestAnimationFrame(() => {
      const node = fieldRefs(field).current[stepIndex];
      if (node) node.setSelectionRange(caret, caret);
      if (node) node.focus();
    });
  };

  const canCreate = name.trim() && accountIds.length > 0 && steps[0].subject !== '' && steps.every((s) => s.variants[0].subject && s.variants[0].body);

  const previewValues = useMemo(() => contactValues(sampleContact), [sampleContact]);
  const previewDefaults = firstNameFallback ? { first_name: firstNameFallback } : {};
  const shownIndex = Math.min(previewStep, steps.length - 1);
  const shownStep = steps[shownIndex];
  const previewSubject = renderPreview(shownStep.variants[0].subject, previewValues, previewDefaults);
  const previewBody = renderPreview(
    optOutLine ? `${shownStep.variants[0].body}\n\n${optOutLine}` : shownStep.variants[0].body,
    previewValues,
    previewDefaults
  );
  const previewMissing = [...new Set([...previewSubject.missing, ...previewBody.missing])];
  const previewHasSpintax =
    hasSpintax(shownStep.variants[0].subject) || hasSpintax(shownStep.variants[0].body);

  const renderSegments = (result) =>
    result.segments.map((segment, index) => (
      <span
        key={index}
        className={segment.missing ? 'campaign-preview-missing' : segment.filled ? 'campaign-preview-filled' : ''}
      >
        {segment.text}
      </span>
    ));

  const create = async ({ startNow }) => {
    setBusy(true);
    setError(null);
    try {
      const campaign = await outreachApi.createCampaign({
        name,
        account_ids: accountIds,
        // Re-numbered here too, not just on delete - the index the
        // dispatcher resolves steps by must never have a gap.
        steps: steps.map((s, i) => ({ ...s, index: i })),
        schedule,
        tracking,
        variable_defaults: firstNameFallback ? { first_name: firstNameFallback } : {},
        opt_out_line: optOutLine,
      });
      if (listId) await outreachApi.enrollContacts(campaign.id, { listId });
      if (startNow) await outreachApi.setCampaignStatus(campaign.id, 'active');
      await onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="campaign-wizard">
      <div className="import-wizard-head">
        <h2 className="unibox-detail-subject">{t('outreach.campaign.create')}</h2>
        <button className="unibox-page-button" onClick={onCancel}>
          {t('outreach.close')}
        </button>
      </div>

      <div className="campaign-wizard-body">
      <div className="campaign-form">

      <div className="campaign-field">
        <label>{t('outreach.campaign.name')}</label>
        <input className="unibox-select" value={name} onChange={(e) => setName(e.target.value)} />
      </div>

      <MailboxPicker
        accounts={accounts}
        selected={accountIds}
        onChange={setAccountIds}
        hint={t('outreach.hints.mailboxes')}
        t={t}
      />

      <div className="campaign-field">
        <label>{t('outreach.campaign.audience')}</label>
        <select className="unibox-select" value={listId} onChange={(e) => setListId(e.target.value)}>
          <option value="">{t('outreach.campaign.noListYet')}</option>
          {lists.map((list) => (
            <option key={list.id} value={list.id}>
              {list.name} ({list.contact_count})
            </option>
          ))}
        </select>
      </div>

      {steps.map((step, index) => (
        <div className="campaign-step" key={index}>
          <div className="campaign-step-head">
            <strong>{t('outreach.campaign.step', { number: index + 1 })}</strong>
            {index > 0 && (
              <>
                <label className="campaign-inline">
                  {t('outreach.campaign.delayDays')}
                  <InfoHint text={t('outreach.hints.delayDays')} />
                  <input
                    className="unibox-select campaign-number"
                    type="number"
                    min="1"
                    value={step.delay_days}
                    onChange={(e) => updateStep(index, { delay_days: Number(e.target.value) })}
                  />
                </label>
                <button
                  className="unibox-page-button"
                  onClick={() =>
                    setSteps(steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, index: i })))
                  }
                >
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </div>
          <div className="campaign-variable-chips">
            {availableVariables.map((field) => (
              <button
                key={field.key}
                type="button"
                className="campaign-variable-chip"
                title={`{{${field.key}}}`}
                onClick={() => insertToken(index, activeFieldByStep[index] || 'body', `{{${field.key}}}`)}
              >
                {variableLabel(field)}
              </button>
            ))}
            <InfoHint text={`${t('outreach.campaign.variables.insertHint')} - ${t('outreach.campaign.variables.customFieldsHint')}`} />
          </div>
          <div className="campaign-variable-field">
            <input
              ref={(el) => (subjectRefs.current[index] = el)}
              className="unibox-select"
              placeholder={t('outreach.campaign.subject')}
              value={step.variants[0].subject}
              onFocus={() => setActiveFieldByStep((current) => ({ ...current, [index]: 'subject' }))}
              onChange={(e) => handleFieldInput(index, 'subject', e)}
            />
            {suggest && suggest.stepIndex === index && suggest.field === 'subject' && (
              <div className="campaign-variable-suggest">
                {suggest.matches.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applySuggestion(item)}
                  >
                    <code>{`{{${item.key}}}`}</code> {variableLabel(item)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="campaign-variable-field">
            <textarea
              ref={(el) => (bodyRefs.current[index] = el)}
              className="unibox-reply-textarea"
              placeholder={t('outreach.campaign.bodyPlaceholder')}
              value={step.variants[0].body}
              onFocus={() => setActiveFieldByStep((current) => ({ ...current, [index]: 'body' }))}
              onChange={(e) => handleFieldInput(index, 'body', e)}
            />
            {suggest && suggest.stepIndex === index && suggest.field === 'body' && (
              <div className="campaign-variable-suggest">
                {suggest.matches.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applySuggestion(item)}
                  >
                    <code>{`{{${item.key}}}`}</code> {variableLabel(item)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}

      <button className="unibox-refresh" onClick={() => setSteps([...steps, emptyStep(steps.length)])}>
        <Plus size={13} /> {t('outreach.campaign.addFollowUp')}
      </button>

      <div className="campaign-field">
        <label>
          {t('outreach.campaign.firstNameFallback')}
          <InfoHint text={t('outreach.hints.fallback')} />
        </label>
        <input
          className="unibox-select"
          value={firstNameFallback}
          onChange={(e) => setFirstNameFallback(e.target.value)}
        />
      </div>

      <div className="campaign-field">
        <label>
          {t('outreach.campaign.optOutLine')}
          <InfoHint text={t('outreach.hints.optOut')} />
        </label>
        <input className="unibox-select" value={optOutLine} onChange={(e) => setOptOutLine(e.target.value)} />
      </div>

      <button className="unibox-refresh" onClick={() => setShowAdvanced(!showAdvanced)}>
        {showAdvanced ? t('outreach.campaign.hideAdvanced') : t('outreach.campaign.showAdvanced')}
      </button>

      {showAdvanced && (
        <div className="campaign-advanced">
          <label className="campaign-inline">
            {t('outreach.campaign.minGap')}
            <InfoHint text={t('outreach.hints.minGap')} />
            <input
              className="unibox-select campaign-number"
              type="number"
              value={schedule.min_gap_seconds / 60}
              onChange={(e) => setSchedule({ ...schedule, min_gap_seconds: Number(e.target.value) * 60 })}
            />
          </label>
          <label className="campaign-inline">
            {t('outreach.campaign.jitter')}
            <InfoHint text={t('outreach.hints.jitter')} />
            <input
              className="unibox-select campaign-number"
              type="number"
              value={schedule.jitter_seconds / 60}
              onChange={(e) => setSchedule({ ...schedule, jitter_seconds: Number(e.target.value) * 60 })}
            />
          </label>
          <label className="campaign-inline">
            {t('outreach.campaign.window')}
            <InfoHint text={t('outreach.hints.window')} />
            <input
              className="unibox-select campaign-number"
              type="time"
              value={schedule.window_start.slice(0, 5)}
              onChange={(e) => setSchedule({ ...schedule, window_start: `${e.target.value}:00` })}
            />
            <input
              className="unibox-select campaign-number"
              type="time"
              value={schedule.window_end.slice(0, 5)}
              onChange={(e) => setSchedule({ ...schedule, window_end: `${e.target.value}:00` })}
            />
          </label>
          <label className="campaign-inline">
            {t('outreach.campaign.dailyCap')}
            <InfoHint text={t('outreach.hints.campaignDailyCap')} />
            <input
              className="unibox-select campaign-number"
              type="number"
              min="1"
              placeholder={t('outreach.campaign.dailyCapUnset')}
              value={schedule.daily_cap_total ?? ''}
              onChange={(e) =>
                setSchedule({
                  ...schedule,
                  daily_cap_total: e.target.value ? Number(e.target.value) : null,
                })
              }
            />
          </label>
          <p className="unibox-reply-hint">
            {t('outreach.campaign.mailboxCeiling', {
              total: accounts
                .filter((a) => accountIds.includes(a.id))
                .reduce((sum, a) => sum + a.limits.daily_cap, 0),
              count: accountIds.length,
            })}
          </p>

          {/* Hidden until OUTREACH_TRACKING_BASE_URL points at a throwaway
              tracking domain - all three put ours in every sent email, and
              prod has none, so ticking one blocks the send (TrackingConfigError).
          <div className="campaign-field">
            <label>{t('outreach.campaign.trackingHeading')}</label>
            <label className="campaign-checkbox">
              <input
                type="checkbox"
                checked={tracking.open}
                onChange={() => setTracking({ ...tracking, open: !tracking.open })}
              />
              {t('outreach.campaign.trackOpens')}
              <InfoHint text={t('outreach.hints.trackOpens')} />
            </label>
            <label className="campaign-checkbox">
              <input
                type="checkbox"
                checked={tracking.click}
                onChange={() => setTracking({ ...tracking, click: !tracking.click })}
              />
              {t('outreach.campaign.trackClicks')}
              <InfoHint text={t('outreach.hints.trackClicks')} />
            </label>
            <label className="campaign-checkbox">
              <input
                type="checkbox"
                checked={tracking.unsub_link}
                onChange={() => setTracking({ ...tracking, unsub_link: !tracking.unsub_link })}
              />
              {t('outreach.campaign.unsubLink')}
              <InfoHint text={t('outreach.hints.unsubLink')} />
            </label>
            {tracking.open && <p className="unibox-reply-hint">{t('outreach.hints.opensForceHtml')}</p>}
          </div>
          */}
        </div>
      )}

      </div>

      <aside className="campaign-preview">
        <div className="campaign-preview-head">
          <strong>{t('outreach.campaign.previewTitle')}</strong>
          {steps.length > 1 && (
            <div className="campaign-preview-nav">
              <button
                type="button"
                className="unibox-page-button"
                onClick={() => setPreviewStep(Math.max(shownIndex - 1, 0))}
                disabled={shownIndex === 0}
                aria-label={t('outreach.previous')}
              >
                <ChevronUp size={14} />
              </button>
              <span className="lead-history-source">
                {t('outreach.campaign.stepOf', { number: shownIndex + 1, total: steps.length })}
              </span>
              <button
                type="button"
                className="unibox-page-button"
                onClick={() => setPreviewStep(Math.min(shownIndex + 1, steps.length - 1))}
                disabled={shownIndex === steps.length - 1}
                aria-label={t('outreach.next')}
              >
                <ChevronDown size={14} />
              </button>
            </div>
          )}
        </div>

        <div className="campaign-preview-mail">
          <div className="campaign-preview-meta">
            {accounts.find((a) => a.id === accountIds[0])?.email || '—'} →{' '}
            {sampleContact ? sampleContact.email : '—'}
          </div>
          <div className={`campaign-preview-subject${shownStep.variants[0].subject ? '' : ' is-placeholder'}`}>
            {shownStep.variants[0].subject
              ? renderSegments(previewSubject)
              : t('outreach.campaign.subject')}
          </div>
          <div className={`campaign-preview-body${shownStep.variants[0].body ? '' : ' is-placeholder'}`}>
            {shownStep.variants[0].body
              ? renderSegments(previewBody)
              : t('outreach.campaign.previewEmptyBody')}
          </div>
        </div>

        {!listId && <p className="unibox-reply-hint">{t('outreach.campaign.previewNoContact')}</p>}
        {sampleContact && (
          <p className="unibox-reply-hint">
            {t('outreach.campaign.previewFor', { email: sampleContact.email })}
          </p>
        )}
        {previewHasSpintax && (
          <p className="unibox-reply-hint">{t('outreach.campaign.previewSpintaxNote')}</p>
        )}
        {previewMissing.map((variable) => (
          <p key={variable} className="unibox-error">
            {t('outreach.campaign.previewUnresolved', { name: `{{${variable}}}` })}
          </p>
        ))}
      </aside>
      </div>

      <div className="campaign-wizard-footer">
        <button className="unibox-button" onClick={() => create({ startNow: true })} disabled={busy || !canCreate}>
          {busy ? t('outreach.campaign.creating') : t('outreach.campaign.createAndStart')}
        </button>
        <button className="unibox-refresh" onClick={() => create({ startNow: false })} disabled={busy || !canCreate}>
          {t('outreach.campaign.saveDraft')}
        </button>
        {error && <span className="unibox-error">{String(error)}</span>}
      </div>
    </div>
  );
}
